import React, {useMemo, useState} from 'react';
import {Button, Dropdown, Modal, Radio, Space} from "antd";
import {usePositionOverride} from "../../providers/PositionOverrideProvider";
import {CheckCircleOutlined, FileDoneOutlined, SendOutlined, SettingOutlined, LinkOutlined, ReloadOutlined, CloseCircleOutlined} from '@ant-design/icons';
import {usePosition} from "../../providers/ProjectPositionProvider";
import Document from "../../models/Document";
import {useSearchParams} from "react-router-dom";
import InfoTooltip from "../../components/InfoTooltip";
import UploadTalentContract from "./UploadTalentContract";
import {useProjectForm} from "../../providers/ProjectFormProvider";
import PositionCandidateGreenlightModal from "./PositionCandidateGreenlightModal";
import {useMount} from "react-use";
import CandidateGreenlightWorkflow from "./CandidateGreenlightRequestWorkflow";
import PositionCandidateGreenlightOptionsModal from "./PositionCandidateGreenlightOptionsModal";
import PositionCandidateHireTypeOptionsModal from "./PositionCandidateHireTypeOptionsModal";

const TYPE_TALENT_CONTRACT = Document.typeContract();
const TYPE_SIGNED_TALENT_CONTRACT = Document.typeSignedContract();

const ContractItem = ({name, file, empty, onRemove, isDisabled, canUpload, onUpload, className = '', ...props}) => {

    return (
        <div className={TB.mapClass("justify-between", {[className]: true})}>
            {name && (
                <p className="whitespace-nowrap max-w-full font-semibold text-xs mb-2 text-light-gray4">
                    {name}
                </p>
            )}
            {file ? (
                <div
                    className="flex flex-col gap-0.5 w-full overflow-hidden items-stretch self-start flex-1 justify-between">
                    <a href={file.path} target="_blank"
                       className="max-w-full text-sm self-start no-underline outline-0 hover:outline-0 hover:text-primary-dark focus:text-primary-dark text-primary inline-flex flex-row items-start gap-1 py-1.5">
                        <FileDoneOutlined className="text-sm"/>
                        <span className="text-center whitespace-nowrap underline overflow-hidden overflow-ellipsis">
                            {file.name}
                        </span>
                    </a>
                    <p className="text-xxs text-black self-start flex-row gap-2 flex">
                        <span>{file.getFileSize()}</span>
                        <span>{TB.moment(file.uploaded_at).format('lll')}</span>
                    </p>
                </div>
            ) : (
                empty && (
                    <p className="h-[50px] text-sm opacity-70 italic py-1.5">
                        {empty}
                    </p>
                )
            )}
            <div className="flex mt-2 flex-row items-center gap-1.5 justify-end">
                {props.children}
                {file ? (
                    <Button className="mx-0" disabled={isDisabled}
                            onClick={() => onRemove()}>
                        <i className="fa-1x mr-1 fas fa-trash"/>
                        <span>Delete</span>
                    </Button>
                ) : (
                    <Button className="mx-0" type="primary" disabled={isDisabled || !canUpload}
                            onClick={() => onUpload()}>
                        <i className="fa-1x mr-1 fas fa-upload"/>
                        <span>Upload</span>
                    </Button>
                )}
            </div>
        </div>
    )
}

const taxClassificationOptions = TB.map(laravel.greenlight_tax_class_preferences, ({label, key}) => ({
    label,
    value: key
}))
const ManageTalentContractForPosition = () => {

    const {position} = usePosition();
    const [isGreenLightRequestModalOpen, setIsGreenLightRequestModalOpen] = useState(false)
    const [isGreenLightOptionsModalOpen, setIsGreenLightOptionsModalOpen] = useState(false)
    const [isHireTypeOptionsModalOpen, setIsHireTypeOptionsModalOpen] = useState(false)
    const {project, onPositionChange, onFormReset, onFormSubmit} = useProjectForm();


    const {
        isLoading,
        isDisabled,
        updateTalentContract,
        handlePositionUpdated,
        updateTaxPreference,
        terminateCandidateForGreenLight,
        refreshGreenlightStatus,
        unlinkGreenlightJobToCandidate,
        onChange
    } = usePositionOverride();


    const candidate = position?.assigned_candidate || {};

    const [searchParams, setSearchParams] = useSearchParams();

    useMount(() => {
        let taxProps = [
            'work_timezone',
            'is_onsite',
            'employed_by',
            'client_tax_preference',
            'can_request_classify_tax',
            'needs_request_classify_tax',
            'tax_classify_status',
            'background_check_status',
            'preferred_tax_class',
            'tax_class',
            'tax_class_confirmed_at',
            'tax_path',
            'tax_workflow',
        ]
        console.log('candidate', TB.pick(candidate, taxProps))
    })
    const isDrawerOpen = useMemo(() => {
        return !isGreenLightRequestModalOpen &&
            searchParams.has('manageTalentContract') &&
            parseInt(searchParams.get('roleId')) === position?.id;
    }, [searchParams?.toString()]);

    const latestSignedContract = useMemo(() => {
        if (!position.latestSignedTalentContract()) return null;
        return Document.make(candidate.job_candidate?.latest_signed_contract)
    }, [position?.latestSignedTalentContract?.(), candidate?.job_candidate?.latest_signed_contract, isDisabled, isLoading]);

    const latestUnsignedContract = useMemo(() => {
        if (!candidate?.job_candidate?.latest_contract) return null;
        return Document.make(candidate.job_candidate?.latest_contract)
    }, [candidate?.job_candidate?.latest_contract, isDisabled, isLoading]);

    const completedTaxPreferenceAndContract = useMemo(() => {
        return position.offeringHireType() && (position.hasCompletedTaxClassificationForCandidate() || latestSignedContract)
    }, [
        isDisabled,
        candidate?.employed_by,
        candidate?.client_tax_preference,
        latestSignedContract
    ]);

    const isContractAvailable = candidate.contract_available_at;

    const resetFormModel = () => {
        return {
            employed_by: candidate.employed_by,
            client_tax_preference: candidate.client_tax_preference,
            is_onsite: candidate.is_onsite,
            work_timezone: candidate.work_timezone,
            document_path: null,
            signed_contract_at: null,
            document_type: TYPE_TALENT_CONTRACT,
        }
    }
    const [formModel, setFormModel] = useState(() => resetFormModel());

    const updateTaxClassification = (updateProps) => {
        let positionIndex = TB.findIndex(project.positions, ['id', position.id]);

        return onPositionChange(positionIndex, updateProps)
    }

    const onRemoveFile = async (type) => {
        let confirm = await TB.sysAlert('talent.delete_proposal_slide_button_clicked');
        if (confirm) {

            return updateTalentContract({
                document_path: null,
                document_type: type,
                signed_contract_at: null,
                contract_available_at: null
            })
        }
    }

    const onOpenDrawer = () => {
        if (isGreenLightRequestModalOpen || searchParams.has('manageTalentContract')) {
            return;
        }

        let params = new URLSearchParams(searchParams);
        params.set('roleId', position.id)
        params.set('manageTalentContract', '1')
        return setSearchParams(params)
    }

    const onOpenUpload = async (document_type) => {
        await setFormModel({...formModel, document_type});
        return onOpenDrawer()
    }

    const onCloseDrawer = async () => {
        if (isGreenLightRequestModalOpen) {
            await setIsGreenLightRequestModalOpen(false)
        }
        let params = new URLSearchParams(searchParams);
        params.delete('roleId')
        params.delete('manageTalentContract')
        await setSearchParams(params)
        return setFormModel(() => resetFormModel())
    }

    const greenlightSettingOptions = [
        {
            label: (
                <div className="flex flex-row items-center gap-2">
                    <ReloadOutlined/>
                    <p className="whitespace-no-wrap">
                        Refresh
                    </p>
                </div>
            ),
            key: 'refresh',
            disabled: isDisabled,
            onClick: () => {
                console.log('refresh')
                if (isDisabled) return;
                return refreshGreenlightStatus()
            }
        },
        {
            label: (
                <div className="flex flex-row items-center gap-2">
                    <LinkOutlined/>
                    <p className="whitespace-no-wrap">
                        Unlink Job
                    </p>
                </div>
            ),
            key: 'unlink',
            disabled: isDisabled,
            onClick: () => unlinkGreenlightJobToCandidate()
        },
        {
            label: (
                <div className="flex flex-row items-center gap-2">
                    <CloseCircleOutlined />
                    <p className="whitespace-no-wrap">
                        Terminate
                    </p>
                </div>
            ),
            key: 'terminate',
            disabled: isDisabled,
            onClick: () => terminateCandidateForGreenLight()
        },
    ];

    return (
        (
            <React.Fragment>
                <div className="flex-grow px-4 relative py-2.5 flex flex-col items-stretch gap-4 bg-light-gray3">


                    {TB.isDefined(candidate) && (
                        <div className={TB.mapClass("flex flex-col items-stretch justify-start relative gap-3", {
                            "pointer-events-none opacity-30": false
                        })}>
                            <div className="flex flex-row justify-start gap-2">
                                <p className="font-semibold">
                                    EOR, Talent Classification & Contract:
                                    {completedTaxPreferenceAndContract ? (
                                        <i className="fas fa-check-circle text-success ml-1.5"/>
                                    ) : (
                                        <i className="text-danger ml-1.5 fas fa-exclamation-circle"/>
                                    )}
                                </p>
                            </div>
                            <div className="flex flex-row items-stretch justify-between">
                                <div className="flex flex-col flex-grow">
                                    <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-no-wrap">
                                        Employer Of Record
                                    </p>

                                    <div>
                                        <Radio.Group
                                            onChange={(e) => setFormModel({...formModel, employed_by: e.target.value})}
                                            disabled={completedTaxPreferenceAndContract || isDisabled}
                                            value={formModel.employed_by || ''}>
                                            <Space direction="vertical">
                                                <Radio value="Wripple">
                                                    <span className="whitespace-no-wrap text-sm">
                                                        Wripple (1099)
                                                    </span>
                                                </Radio>
                                                <Radio value="GreenLight">
                                                    <span className="whitespace-no-wrap text-sm">
                                                         GreenLight
                                                    </span>
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </div>
                                    <Button
                                        // type="default"
                                        disabled={isDisabled || !formModel.employed_by || candidate.employed_by === formModel.employed_by}
                                        onClick={async () => {

                                            if (formModel.employed_by === 'GreenLight') {
                                                let updated = await updateTaxPreference({employed_by: formModel.employed_by});
                                                return handlePositionUpdated({
                                                    ...position,
                                                    assigned_candidate: {
                                                        ...candidate,
                                                        employed_by: formModel.employed_by,
                                                        needs_request_classify_tax: true,
                                                        can_request_classify_tax: true,
                                                        tax_classify_status: "Not Requested",
                                                    }
                                                })
                                            }
                                            let updated = await updateTaxPreference({employed_by: formModel.employed_by});

                                            if (updated) {
                                                TB.toast("Position Tax Classification Updated")
                                            }

                                        }}
                                        className={TB.mapClass("mt-5 ml-0 self-start", {

                                            "bg-black text-white": !isDisabled && formModel.employed_by && candidate.employed_by !== formModel.employed_by,
                                        })}>
                                        Update
                                    </Button>
                                </div>

                                {candidate.employed_by && (
                                    <React.Fragment>
                                        <div className="w-[1px] bg-gray-400 mx-2"/>
                                        {candidate.employed_by === 'Wripple' ? (
                                            <React.Fragment>
                                                {(latestUnsignedContract || !latestSignedContract) && (
                                                    <React.Fragment>
                                                        <ContractItem
                                                            name="Unsigned Talent Contract"
                                                            className="flex flex-col min-w-[265px]"
                                                            file={latestUnsignedContract}
                                                            empty={!latestSignedContract && (
                                                                <span><i
                                                                    className="fas fa-exclamation-circle mr-0.5"/> Missing</span>
                                                            )}
                                                            isDisabled={isDisabled}
                                                            canUpload={!latestSignedContract}
                                                            onUpload={() => onOpenUpload(TYPE_TALENT_CONTRACT)}
                                                            onRemove={() => onRemoveFile(latestUnsignedContract.type)}
                                                        >

                                                            <div
                                                                className="relative flex-grow mx-0 flex flex-col items-stretch">
                                                                <Button
                                                                    type={isContractAvailable ? undefined : 'primary'}
                                                                    disabled={isDisabled || !latestUnsignedContract || isContractAvailable || latestSignedContract}
                                                                    icon={isContractAvailable ? (
                                                                        <CheckCircleOutlined style={{fontSize: 12}}/>
                                                                    ) : (
                                                                        <SendOutlined style={{fontSize: 12}}/>
                                                                    )}
                                                                    onClick={() => updateTalentContract({contract_available_at: new Date()})}
                                                                    loading={isDisabled}
                                                                    className={TB.mapClass("flex-grow mx-0", {
                                                                        "cursor-default": isContractAvailable || latestSignedContract,
                                                                    })}>

                                                                    {!isContractAvailable ? "Make Available To Sign" : "Contract Available"}

                                                                </Button>

                                                                {isContractAvailable && (
                                                                    <div
                                                                        id={`available-${position?.id}-${candidate?.id}`}
                                                                        className="absolute inset-0 z-[1px]"/>
                                                                )}

                                                            </div>
                                                            {isContractAvailable && (
                                                                <InfoTooltip type="dark" placement="top"
                                                                             target={`available-${position?.id}-${candidate?.id}`}>
                                                                    <p className="text-white text-xs px-1.5 py-0.5">
                                                                        Made available for talent to download and
                                                                        sign: {TB.moment(isContractAvailable).format('lll')}
                                                                    </p>
                                                                </InfoTooltip>
                                                            )}


                                                        </ContractItem>
                                                        <div className="w-0.5 bg-gray-400 mx-3"/>
                                                    </React.Fragment>
                                                )}
                                                <ContractItem
                                                    name="Signed Talent Contract"
                                                    className="flex flex-col min-w-[265px]"
                                                    file={latestSignedContract}
                                                    empty={latestUnsignedContract ? (
                                                        isContractAvailable ?
                                                            <span><i
                                                                className="fas fa-history mr-0.5"/> Waiting for Signature</span> :
                                                            <span><i className="fas fa-info-circle mr-0.5"/> Ready, Not Sent</span>
                                                    ) : (
                                                        <span><i
                                                            className="fas fa-exclamation-circle mr-0.5"/> Missing</span>
                                                    )}
                                                    isDisabled={isDisabled}
                                                    canUpload={!latestUnsignedContract}
                                                    onUpload={() => onOpenUpload(TYPE_SIGNED_TALENT_CONTRACT)}
                                                    onRemove={() => onRemoveFile(TYPE_SIGNED_TALENT_CONTRACT)}
                                                />
                                            </React.Fragment>
                                        ) : (
                                            <div className="flex flex-col flex-grow">
                                                <div className="flex flex-row items-center justify-between">
                                                    <p className="pb-2 font-semibold text-xs text-light-gray4">
                                                        GreenLight Tax Classification
                                                    </p>
                                                    {candidate.greenlight_id && (
                                                        <div className="flex flex-row items-center gap-3">

                                                            <Dropdown
                                                                menu={{
                                                                    items: greenlightSettingOptions,
                                                                    onClick: ({key}) => {
                                                                        // let option = TB.find(greenlightSettingOptions, ['key', key]);

                                                                        // console.log('onClick', option)
                                                                        // return option.onClick();
                                                                    }
                                                                }}
                                                                trigger={['hover']}
                                                                placement="bottomRight"
                                                            >
                                                                <div className="flex flex-row gap-1 items-center clickable">

                                                                    <p className="text-xs">Settings</p>
                                                                    <SettingOutlined style={{fontSize: 17}}
                                                                                     className="text-gray-600"/>
                                                                </div>

                                                            </Dropdown>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-stretch gap-2.5 min-w-[350px]">
                                                    {!candidate.needs_request_classify_tax && candidate.tax_classify_status && (
                                                        <p className="text-xs">
                                                            <span>Current Status: </span>
                                                            <span className={TB.mapClass("font-semibold", {
                                                                "text-danger": candidate?.tax_classify_status !== "Passed"
                                                            })}>
                                                                {candidate.tax_classify_status}
                                                            </span>
                                                        </p>
                                                    )}
                                                    <p className="text-xs">
                                                        <span>Preferred Tax Class: </span>
                                                        <span className="font-semibold">
                                                            {position.preferredTaxClass()}
                                                        </span>
                                                    </p>
                                                    {!candidate.needs_request_classify_tax && (
                                                        <div className="flex flex-row justify-between items-center">
                                                            <p className="text-xs">
                                                                <span>Confirmed Tax Class: </span>
                                                                <span className={TB.mapClass({
                                                                    "font-semibold": !!candidate.tax_class,
                                                                    "text-gray": !candidate.tax_class
                                                                })}>
                                                                {candidate.tax_class || 'Unknown'}
                                                            </span>
                                                            </p>
                                                            <p className="text-xs">
                                                                <span>GL Job ID: </span>
                                                                <span className="text-info font-semibold">
                                                                {candidate.greenlight_id || 'N/A'}
                                                            </span>
                                                            </p>
                                                        </div>
                                                    )}

                                                </div>
                                                <hr className="my-3"/>
                                                <div className="flex flex-row justify-between items-start">
                                                    {!position.candidateNeedsGreenlightRequest() && candidate.tax_workflow?.length > 0 && (
                                                        <React.Fragment>
                                                            <div className="flex flex-col items-start gap-2">
                                                                <p className="whitespace-no-wrap text-xs">
                                                                    Last Recorded Event:
                                                                </p>
                                                            </div>
                                                            <div
                                                                className="flex flex-row justify-start gap-4 relative items-center">
                                                                <div className="flex flex-col gap-0.5">
                                                                    <p className="whitespace-no-wrap text-xs font-semibold">
                                                                        {TB.last(candidate.tax_workflow).name}
                                                                    </p>
                                                                    <p className="whitespace-no-wrap text-xs font-semibold">
                                                                        {TB.moment(TB.last(candidate.tax_workflow).triggered_at).format('ll @ h:mmA')}
                                                                    </p>

                                                                    {TB.last(candidate.tax_workflow).updates.map((update, j) => (
                                                                        <div key={`workflow-${candidate.id}-${j}`}
                                                                             className={`text-xxs text-gray-600 font-semibold ${j === 0 ? 'mt-1' : ''}`}>
                                                                            {update.label}: {update.value}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="flex flex-row gap-2.5 items-center">
                                                                    {/*<Button*/}
                                                                    {/*    type="default"*/}
                                                                    {/*    size="small"*/}
                                                                    {/*    disabled={isDisabled || latestSignedContract}*/}
                                                                    {/*    onClick={() => refreshGreenlightStatus()}*/}
                                                                    {/*    className={TB.mapClass("text-xs ml-0 self-center", {*/}
                                                                    {/*        "cursor-default": latestSignedContract,*/}
                                                                    {/*    })}>*/}
                                                                    {/*    Refresh*/}
                                                                    {/*</Button>*/}
                                                                    <Button
                                                                        type="primary"
                                                                        disabled={isDisabled || latestSignedContract}
                                                                        onClick={() => setIsGreenLightRequestModalOpen(true)}
                                                                        className={TB.mapClass("ml-0 self-center", {
                                                                            "cursor-default": latestSignedContract,
                                                                        })}>
                                                                        Show All Events
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </React.Fragment>
                                                    )}


                                                </div>

                                                {position.candidateNeedsGreenlightRequest() ? (
                                                    <div
                                                        className="m-auto flex flex-row gap-3 items-center justify-center mt-5">
                                                        <Button
                                                            type="default"
                                                            disabled={isDisabled || latestSignedContract}
                                                            onClick={() => setIsGreenLightOptionsModalOpen(true)}
                                                            className={TB.mapClass("ml-0 self-center", {
                                                                "cursor-default": latestSignedContract,
                                                            })}>
                                                            Find Existing Job
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            disabled={isDisabled || latestSignedContract}
                                                            onClick={() => setIsGreenLightRequestModalOpen(true)}
                                                            className={TB.mapClass("ml-0 self-center", {
                                                                "cursor-default": latestSignedContract,
                                                            })}>
                                                            Submit New Request
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-start">

                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                            {candidate.employed_by && (
                                <React.Fragment>
                                    <div className="h-[1px] bg-gray-400 mt-2 mb-2.5"/>
                                    <div className="flex flex-row gap-3 self-stretch items-center justify-start">
                                        <p className="font-semibold text-xs text-light-gray4 whitespace-no-wrap">
                                            Offering Hire Type
                                        </p>

                                        {!position.offeringHireType() ? (

                                            <p className="italic font-semibold text-xs text-danger whitespace-no-wrap">
                                                Missing!
                                            </p>
                                        ) : (

                                            <p className="font-semibold text-xs text-danger whitespace-no-wrap">
                                                {position.offeringHireType()}
                                            </p>
                                        )}


                                        <Button
                                            disabled={isDisabled}
                                            onClick={() => setIsHireTypeOptionsModalOpen(true)}
                                            className={TB.mapClass("m-0", {
                                                "bg-black text-white": !isDisabled
                                            })}>
                                            {position.offeringHireType() ? 'Update' : 'Select'} Hire Type
                                        </Button>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    )}

                </div>
                <UploadTalentContract
                    isDisabled={isDisabled || isLoading}
                    setFormModel={setFormModel}
                    formModel={formModel}
                    onUpload={updateTalentContract}
                    isOpen={isDrawerOpen}
                    onClose={onCloseDrawer}
                />
                <Modal open={isGreenLightRequestModalOpen}
                       width={candidate.can_request_classify_tax ? 900 : 650}
                       title={false}
                       footer={false}
                       className="greenlight-request-modal"
                       closable={!isDisabled && true}
                       maskClosable={!isDisabled && true}
                       destroyOnClose={true}
                       onCancel={() => setIsGreenLightRequestModalOpen(false)}
                       onClose={() => setIsGreenLightRequestModalOpen(false)}>

                    {!candidate.can_request_classify_tax ? (
                        <CandidateGreenlightWorkflow
                            onUpdate={handlePositionUpdated}
                            onClose={() => setIsGreenLightRequestModalOpen(false)}/>
                    ) : (
                        <PositionCandidateGreenlightModal
                            onUpdate={handlePositionUpdated}
                            onClose={() => setIsGreenLightRequestModalOpen(false)}/>
                    )}

                </Modal>
                <Modal
                    okText="Submit"
                    title={"Link to Existing GreenLight Job"}
                    closable={true}
                    maskClosable={false}
                    style={{top: 50}}
                    destroyOnClose={true}
                    width={700}
                    footer={null}
                    open={isGreenLightOptionsModalOpen}
                    onCancel={() => setIsGreenLightOptionsModalOpen(false)}
                    onClose={() => setIsGreenLightOptionsModalOpen(false)}>

                    <PositionCandidateGreenlightOptionsModal
                        onClose={() => setIsGreenLightOptionsModalOpen(false)}
                    />

                </Modal>

                <Modal
                    okText="Submit"
                    title={"Candidate Offering Hire Type"}
                    closable={true}
                    maskClosable={false}
                    style={{top: 50}}
                    destroyOnClose={true}
                    width={700}
                    footer={null}
                    open={isHireTypeOptionsModalOpen}
                    onCancel={() => setIsHireTypeOptionsModalOpen(false)}
                    onClose={() => setIsHireTypeOptionsModalOpen(false)}>

                    <PositionCandidateHireTypeOptionsModal
                        onClose={() => setIsHireTypeOptionsModalOpen(false)}
                    />

                </Modal>

            </React.Fragment>
        )
    )
        ;
};

export {ContractItem}
export default ManageTalentContractForPosition;